import type { EvaluatedElement } from "@vera/rules";
import type { Violation } from "@vera/shared";
import type { SheetInfo } from "../data/sheets";
import { BCA_513_RULE_ID } from "../viewer/geometry/types";
import { findSheet } from "./activeSheets";

const SPATIAL_ONTOLOGY_TYPES = new Set([
  "vera:AccessibleWaterCloset",
  "vera:Stair",
  "vera:Handrail",
  "vera:Guard",
]);

export interface ViolationViewTarget {
  sheetId: string;
  sheetName: string;
  kind: "2d" | "3d";
  /** Short label for violation list badges. */
  badge: string;
  /** Tooltip / aria description. */
  hint: string;
  /** Switch away from the current sheet when the violation is selected. */
  autoSwitch: boolean;
}

/** Element types that need the 3D model for a useful highlight. */
export function needsSpatialModelView(
  element?: EvaluatedElement,
  ruleId?: string
): boolean {
  if (ruleId === BCA_513_RULE_ID) return true;
  if (!element) return false;
  return SPATIAL_ONTOLOGY_TYPES.has(element.ontologyType);
}

export function resolveViolationViewTarget(
  violation: Violation,
  sheets: SheetInfo[],
  activeSheetId: string,
  element?: EvaluatedElement
): ViolationViewTarget | null {
  const ruleId = violation.provenance?.ruleId;
  const spatial = needsSpatialModelView(element, ruleId);
  const sheet3d = sheets.find(s => s.kind === "3d");
  const preferred = findSheet(sheets, violation.preferredSheetId);

  let targetSheet: SheetInfo | undefined;
  if (spatial && sheet3d) {
    targetSheet = sheet3d;
  } else {
    targetSheet = preferred ?? sheets.find(s => s.kind === "2d") ?? sheet3d;
  }
  if (!targetSheet) return null;

  const onTarget = targetSheet.id === activeSheetId;
  const badge =
    targetSheet.kind === "3d"
      ? onTarget
        ? "3D model"
        : "Opens in 3D"
      : onTarget
        ? targetSheet.name
        : targetSheet.name;

  const hint =
    targetSheet.kind === "3d"
      ? spatial
        ? "Spatial check — opens the 3D building model"
        : "Opens the 3D building model"
      : onTarget
        ? "Shown on this sheet"
        : `Opens sheet ${targetSheet.name}`;

  return {
    sheetId: targetSheet.id,
    sheetName: targetSheet.name,
    kind: targetSheet.kind,
    badge,
    hint,
    autoSwitch: !onTarget,
  };
}

/** Whether selecting this violation should change the active sheet/view. */
export function shouldNavigateToViolation(
  target: ViolationViewTarget,
  activeSheetId: string,
  onUniversalView: boolean
): boolean {
  if (!target.autoSwitch || target.sheetId === activeSheetId) return false;
  if (onUniversalView) return false;
  return true;
}
